/**
 * AnalyticsInsights — the "smart" centrepiece of the Analytics page.
 * Renders the one-line headline verdict + ranked, plain-English findings
 * split into "What's working" (good) and "What's hurting" (bad/warn).
 * Info-only items (e.g. "need more data") render as a full-width note.
 */
import React from 'react';
import { Box, Card, CardContent, Typography, Chip, Grid, Tooltip } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { useAppTheme } from '../../hooks/useAppTheme';
import { InsightItem } from '../../services/analytics.service';

const AnalyticsInsights: React.FC<{ insights: InsightItem[]; headline: string; totalPnl: number }> = ({ insights, headline, totalPnl }) => {
  const { C } = useAppTheme();

  const meta = (sev: InsightItem['severity']) => {
    switch (sev) {
      case 'good': return { color: C.bull, bg: C.bullMuted, border: C.bullBorder, Icon: CheckCircleRoundedIcon };
      case 'bad':  return { color: C.bear, bg: C.bearMuted, border: C.bearBorder, Icon: TrendingDownRoundedIcon };
      case 'warn': return { color: C.warn, bg: C.warnMuted, border: C.warnBorder, Icon: WarningAmberRoundedIcon };
      default:     return { color: C.neutral, bg: C.neutralMuted, border: C.neutralBorder, Icon: InfoRoundedIcon };
    }
  };

  const good = insights.filter(i => i.severity === 'good');
  const bad  = insights.filter(i => i.severity === 'bad' || i.severity === 'warn');
  const info = insights.filter(i => i.severity === 'info');

  const renderCard = (i: InsightItem) => {
    const m = meta(i.severity);
    return (
      <Box key={i.id} sx={{
        display: 'flex', gap: 1, p: 1.25, mb: 1, borderRadius: 1.5,
        bgcolor: m.bg, border: `1px solid ${m.border}`,
      }}>
        <m.Icon sx={{ fontSize: 20, color: m.color, mt: 0.2, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: m.color }}>{i.title}</Typography>
            <Box sx={{ flex: 1 }} />
            <Tooltip arrow title={`Based on ${i.sampleN} trades — confidence grows with sample size`}>
              <Chip label={`n=${i.sampleN}`} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: 'transparent', border: `1px solid ${m.border}`, color: m.color }} />
            </Tooltip>
          </Box>
          <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.25, lineHeight: 1.45 }}>
            {i.detail}
          </Typography>
        </Box>
      </Box>
    );
  };

  const headlinePositive = totalPnl >= 0;

  return (
    <Card sx={{ borderLeft: `4px solid ${headlinePositive ? C.bull : C.bear}`, borderRadius: 1.5 }}>
      <CardContent sx={{ pb: '12px !important' }}>
        {/* Headline verdict */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}>
            Key findings
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: headlinePositive ? C.bull : C.bear }}>
            {headline}
          </Typography>
        </Box>

        {info.length > 0 && info.map(renderCard)}

        {(good.length > 0 || bad.length > 0) && (
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.72rem', color: C.bull, mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                ✓ What's working
              </Typography>
              {good.length ? good.map(renderCard) : (
                <Typography variant="caption" color="text.secondary">No clear positives in this window yet.</Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.72rem', color: C.bear, mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                ⚠ What's hurting
              </Typography>
              {bad.length ? bad.map(renderCard) : (
                <Typography variant="caption" color="text.secondary">No major problems detected. 🎉</Typography>
              )}
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalyticsInsights;
